import {
  createEffect,
  createSignal,
  For,
  Show,
  type JSX,
} from "solid-js";
import { DumpIcon } from "../components/DumpIcon";
import { RichTextEditor } from "../components/RichTextEditor";
import { apiRequest } from "../core/api";
import { currentUser, hasPermission } from "../core/session";
import { CrudCollectionPage, type CrudRecord } from "./CrudCollectionPage";

type DocketSection =
  | "Comments"
  | "Motions"
  | "Trials"
  | "Evidence"
  | "Trial Notes"
  | "History"
  | "Availability";

export function DocketPage() {
  return (
    <CrudCollectionPage
      label="Docket"
      singular="Docket"
      endpoint="/dockets"
      icon="nav_docket"
      canCreate={hasPermission("dockets.create")}
      canUpdate={hasPermission("dockets.update")}
      canDelete={hasPermission("dockets.delete")}
      advancedSearch={false}
      createDefaults={{
        caseNumber: "",
        caseType: "criminal",
        title: "",
        description: "",
        status: "posted",
        assignedTo: null,
        outcome: "",
      }}
      fields={[
        { key: "caseNumber", label: "Case Number" },
        { key: "caseType", label: "Case Type" },
        { key: "title", label: "Title" },
        { key: "description", label: "Description" },
        { key: "status", label: "Status" },
        { key: "assignedTo", label: "Assigned User", type: "number" },
        { key: "outcome", label: "Outcome" },
      ]}
      itemTitle={(item) =>
        `${value(item.caseNumber)} - ${value(item.title)}`
      }
      itemDescription={(item) =>
        `${capitalize(value(item.caseType))} • ${value(item.status)
          .replaceAll("_", " ")
          .toUpperCase()}`
      }
      itemRightLabel={(item) =>
        `${value(item.creatorFirstName)} ${value(item.creatorLastName)} - ${date(item.createdAt)}`
      }
      prepareWrite={(item) => ({
        caseNumber: value(item.caseNumber),
        caseType: value(item.caseType) || "criminal",
        title: value(item.title),
        description: value(item.description),
        status: value(item.status) || "posted",
        assignedTo: nullableNumber(item.assignedTo),
        outcome: value(item.outcome),
      })}
      renderAfterFields={(item, reload) => (
        <DocketRelations item={item} reload={reload} />
      )}
    />
  );
}

function DocketRelations(props: {
  item: CrudRecord;
  reload: () => Promise<void>;
}) {
  const [section, setSection] = createSignal<DocketSection>("Comments");
  const [comments, setComments] = createSignal<CrudRecord[]>([]);
  const [motions, setMotions] = createSignal<CrudRecord[]>([]);
  const [trials, setTrials] = createSignal<CrudRecord[]>([]);
  const [evidence, setEvidence] = createSignal<CrudRecord[]>([]);
  const [history, setHistory] = createSignal<CrudRecord[]>([]);
  const [availability, setAvailability] = createSignal<CrudRecord[]>([]);
  const [notes, setNotes] = createSignal("");
  const [subscription, setSubscription] = createSignal(false);
  const [entry, setEntry] = createSignal("");
  const [secondary, setSecondary] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const id = () => Number(props.item.id);

  const load = async () => {
    if (!id()) return;
    const token = currentUser()?.accessToken;
    const [commentsResult, motionsResult, trialsResult, evidenceResult, historyResult, availabilityResult, notesResult, subscribed] =
      await Promise.all([
        read(`/dockets/${id()}/comments`, token),
        read(`/dockets/${id()}/motions`, token),
        read(`/dockets/${id()}/trials`, token),
        read(`/dockets/${id()}/evidence`, token),
        read(`/dockets/${id()}/history`, token),
        read(`/dockets/${id()}/availability-match`, token),
        apiRequest<string>(`/dockets/${id()}/trial-notes`, { token }).catch(
          () => "",
        ),
        apiRequest<boolean>(`/dockets/${id()}/subscription`, { token }).catch(
          () => false,
        ),
      ]);
    setComments(commentsResult);
    setMotions(motionsResult);
    setTrials(trialsResult);
    setEvidence(evidenceResult);
    setHistory(historyResult);
    setAvailability(availabilityResult);
    setNotes(notesResult);
    setSubscription(subscribed);
  };

  createEffect(() => {
    props.item.id;
    void load();
  });

  const submit = async () => {
    if (!entry().trim() || !id()) return;
    setBusy(true);
    try {
      const token = currentUser()?.accessToken;
      if (section() === "Comments") {
        await apiRequest(`/dockets/${id()}/comments`, {
          method: "POST",
          token,
          body: { comment: entry() },
        });
      } else if (section() === "Motions") {
        await apiRequest(`/dockets/${id()}/motions`, {
          method: "POST",
          token,
          body: { title: entry(), body: secondary() },
        });
      } else if (section() === "Trials") {
        await apiRequest(`/dockets/${id()}/trials`, {
          method: "POST",
          token,
          body: {
            startsAt: new Date(entry()).toISOString(),
            endsAt: null,
            location: secondary(),
          },
        });
      } else if (section() === "Evidence") {
        await apiRequest(`/dockets/${id()}/evidence`, {
          method: "POST",
          token,
          body: { evidenceId: Number(entry()) },
        });
      }
      setEntry("");
      setSecondary("");
      await load();
    } finally {
      setBusy(false);
    }
  };

  const saveNotes = async () => {
    setBusy(true);
    try {
      await apiRequest(`/dockets/${id()}/trial-notes`, {
        method: "PUT",
        token: currentUser()?.accessToken,
        body: { content: notes() },
      });
    } finally {
      setBusy(false);
    }
  };

  const toggleSubscription = async () => {
    const next = !subscription();
    setBusy(true);
    try {
      await apiRequest(
        `/dockets/${id()}/${next ? "subscribe" : "unsubscribe"}`,
        { method: "POST", token: currentUser()?.accessToken },
      );
      setSubscription(next);
    } finally {
      setBusy(false);
    }
  };

  const mutate = async (
    path: string,
    method: "PATCH" | "DELETE",
    body?: CrudRecord,
  ) => {
    setBusy(true);
    try {
      await apiRequest(path, {
        method,
        token: currentUser()?.accessToken,
        body,
      });
      await load();
    } finally {
      setBusy(false);
    }
  };

  const removeParticipant = async (citizenId: number) => {
    await mutate(`/dockets/${id()}/participants/${citizenId}`, "DELETE");
    await props.reload();
  };

  const participants = () =>
    Array.isArray(props.item.participants)
      ? (props.item.participants as CrudRecord[])
      : [];

  const canCompose = () =>
    ["Comments", "Motions", "Trials", "Evidence"].includes(section());

  return (
    <section class="bg-[rgba(255,255,255,.04)]">
      <div class="flex items-center justify-between border-b border-white-5 p-3">
        <div class="flex gap-1">
          <For
            each={
              [
                "Comments",
                "Motions",
                "Trials",
                "Evidence",
                "Trial Notes",
                "History",
                "Availability",
              ] as DocketSection[]
            }
          >
            {(item) => (
              <button
                class={`px-3 py-2 text-sm ${
                  section() === item
                    ? "border-b-2 border-cyan bg-white-11 text-cyan"
                    : "bg-gray-gradient text-white-55"
                }`}
                onClick={() => {
                  setSection(item);
                  setEntry("");
                  setSecondary("");
                }}
              >
                {item}
              </button>
            )}
          </For>
        </div>
        <button
          class={`px-3 py-2 text-sm ${
            subscription() ? "bg-green-gradient" : "bg-white-11"
          }`}
          disabled={busy()}
          onClick={() => void toggleSubscription()}
        >
          {subscription() ? "Subscribed" : "Subscribe"}
        </button>
      </div>

      <div class="p-3">
        <Show when={participants().length > 0}>
          <div class="mb-3">
            <p class="mb-2 text-xs uppercase text-white-35">Participants</p>
            <div class="flex flex-wrap gap-2">
              <For each={participants()}>
                {(participant) => (
                  <span class="flex items-center gap-2 bg-gray-gradient px-3 py-2 text-sm">
                    {value(participant.name)} ({value(participant.role)})
                    <Show when={Number(participant.citizenId) > 0}>
                      <button
                        aria-label="Remove participant"
                        disabled={busy()}
                        onClick={() =>
                          void removeParticipant(Number(participant.citizenId))
                        }
                      >
                        <DumpIcon name="tab_close" />
                      </button>
                    </Show>
                  </span>
                )}
              </For>
            </div>
          </div>
        </Show>
        <Show
          when={section() !== "Trial Notes"}
          fallback={
            <div class="flex flex-col gap-3">
              <RichTextEditor
                value={notes()}
                resetKey={id()}
                roomName={id() ? `docket-trial-notes-${id()}` : undefined}
                onChange={setNotes}
              />
              <button
                class="self-end bg-green-gradient px-5 py-2"
                disabled={busy()}
                onClick={() => void saveNotes()}
              >
                Save Trial Notes
              </button>
            </div>
          }
        >
          <Show when={canCompose()}>
            <Composer
              section={section()}
              entry={entry()}
              secondary={secondary()}
              busy={busy()}
              onEntry={setEntry}
              onSecondary={setSecondary}
              onSubmit={() => void submit()}
            />
          </Show>
          <div class="mt-3 flex flex-col gap-2">
            <For
              each={sectionRecords(
                section(),
                comments(),
                motions(),
                trials(),
                evidence(),
                history(),
                availability(),
              )}
            >
              {(record) => (
                <DocketRelationRow
                  section={section()}
                  record={record}
                  busy={busy()}
                  docketId={id()}
                  mutate={mutate}
                />
              )}
            </For>
          </div>
        </Show>
      </div>
    </section>
  );
}

function Composer(props: {
  section: DocketSection;
  entry: string;
  secondary: string;
  busy: boolean;
  onEntry: (value: string) => void;
  onSecondary: (value: string) => void;
  onSubmit: () => void;
}) {
  const inputType = () => (props.section === "Trials" ? "datetime-local" : "text");
  const placeholder = () =>
    props.section === "Comments"
      ? "Write a comment"
      : props.section === "Motions"
        ? "Motion title"
        : props.section === "Trials"
          ? "Trial date and time"
          : "Evidence ID";
  const secondaryPlaceholder = () =>
    props.section === "Motions" ? "Motion body" : "Location";
  return (
    <div class="flex gap-2">
      <input
        class="h-9 flex-1 bg-white-11 px-3"
        type={inputType()}
        placeholder={placeholder()}
        value={props.entry}
        onInput={(event) => props.onEntry(event.currentTarget.value)}
      />
      <Show when={props.section === "Motions" || props.section === "Trials"}>
        <input
          class="h-9 flex-1 bg-white-11 px-3"
          placeholder={secondaryPlaceholder()}
          value={props.secondary}
          onInput={(event) => props.onSecondary(event.currentTarget.value)}
        />
      </Show>
      <button
        class="flex h-9 items-center bg-green-gradient px-4 disabled:opacity-40"
        disabled={props.busy || !props.entry.trim()}
        onClick={props.onSubmit}
      >
        <DumpIcon name="plus" />
        <span class="pl-2">Add</span>
      </button>
    </div>
  );
}

function DocketRelationRow(props: {
  section: DocketSection;
  record: CrudRecord;
  docketId: number;
  busy: boolean;
  mutate: (
    path: string,
    method: "PATCH" | "DELETE",
    body?: CrudRecord,
  ) => Promise<void>;
}) {
  const relationId = () => Number(props.record.id);
  const motionStatus = (status: "approved" | "denied") => {
    const reason =
      status === "denied"
        ? window.prompt("Reason for denying this motion", "") ?? ""
        : "";
    return props.mutate(
      `/dockets/${props.docketId}/motions/${relationId()}`,
      "PATCH",
      { status, reason },
    );
  };
  const updateOutcome = () => {
    const outcome = window.prompt(
      "Trial outcome",
      value(props.record.outcome),
    );
    if (outcome === null) return Promise.resolve();
    return props.mutate(
      `/dockets/${props.docketId}/trials/${relationId()}`,
      "PATCH",
      { outcome },
    );
  };

  return (
    <div class="item-list-bg flex items-start justify-between gap-3 bg-gray-gradient px-3 py-2">
      <div class="min-w-0">
        <p>{recordTitle(props.section, props.record)}</p>
        <p class="whitespace-pre-wrap text-sm text-white-55">
          {recordDescription(props.section, props.record)}
        </p>
      </div>
      <div class="flex shrink-0 gap-1">
        <Show when={props.section === "Comments"}>
          <RelationButton
            label="Delete"
            danger
            disabled={props.busy}
            onClick={() =>
              void props.mutate(
                `/dockets/${props.docketId}/comments/${relationId()}`,
                "DELETE",
              )
            }
          />
        </Show>
        <Show when={props.section === "Motions"}>
          <RelationButton
            label="Approve"
            disabled={props.busy}
            onClick={() => void motionStatus("approved")}
          />
          <RelationButton
            label="Deny"
            danger
            disabled={props.busy}
            onClick={() => void motionStatus("denied")}
          />
        </Show>
        <Show when={props.section === "Trials"}>
          <RelationButton
            label="Outcome"
            disabled={props.busy}
            onClick={() => void updateOutcome()}
          />
          <RelationButton
            label="Delete"
            danger
            disabled={props.busy}
            onClick={() =>
              void props.mutate(
                `/dockets/${props.docketId}/trials/${relationId()}`,
                "DELETE",
              )
            }
          />
        </Show>
        <Show
          when={
            props.section === "Evidence" &&
            !props.record.approvedBy &&
            !props.record.approved_by
          }
        >
          <RelationButton
            label="Approve"
            disabled={props.busy}
            onClick={() =>
              void props.mutate(
                `/dockets/${props.docketId}/evidence/${relationId()}/approve`,
                "PATCH",
              )
            }
          />
        </Show>
      </div>
    </div>
  );
}

function RelationButton(props: {
  label: string;
  danger?: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      class={`px-3 py-1 text-xs disabled:opacity-40 ${
        props.danger ? "bg-red-gradient" : "bg-green-gradient"
      }`}
      disabled={props.disabled}
      onClick={props.onClick}
    >
      {props.label}
    </button>
  );
}

function sectionRecords(
  section: DocketSection,
  comments: CrudRecord[],
  motions: CrudRecord[],
  trials: CrudRecord[],
  evidence: CrudRecord[],
  history: CrudRecord[],
  availability: CrudRecord[],
) {
  if (section === "Comments") return comments;
  if (section === "Motions") return motions;
  if (section === "Trials") return trials;
  if (section === "Evidence") return evidence;
  if (section === "History") return history;
  if (section === "Availability") return availability;
  return [];
}

function recordTitle(section: DocketSection, item: CrudRecord): string {
  if (section === "Comments") return `${value(item.userName)} • ${date(item.createdAt)}`;
  if (section === "Motions") return `${value(item.title)} • ${value(item.status)}`;
  if (section === "Trials") return `${date(item.startsAt)} • ${value(item.location)}`;
  if (section === "History") return `${value(item.action)} • ${value(item.actor)}`;
  if (section === "Availability") return value(item.name);
  return `${value(item.type)}: ${value(item.identifier)}`;
}

function recordDescription(section: DocketSection, item: CrudRecord): string {
  if (section === "Comments") return value(item.comment);
  if (section === "Motions") return value(item.body);
  if (section === "Trials") return value(item.outcome) || "No outcome recorded";
  if (section === "History")
    return `${value(item.details)} • ${date(item.createdAt)}`;
  if (section === "Availability") {
    const slots = Array.isArray(item.slots)
      ? (item.slots as CrudRecord[])
      : [];
    return slots.length
      ? slots
          .map(
            (slot) =>
              `${weekday(Number(slot.dayOfWeek))} ${String(slot.hour).padStart(2, "0")}:00`,
          )
          .join(", ")
      : "No availability recorded";
  }
  return value(item.description);
}

async function read(path: string, token?: string): Promise<CrudRecord[]> {
  try {
    return await apiRequest<CrudRecord[]>(path, { token });
  } catch {
    return [];
  }
}

function value(input: unknown): string {
  return typeof input === "string" || typeof input === "number"
    ? String(input)
    : "";
}
function nullableNumber(input: unknown): number | null {
  const output = Number(input);
  return Number.isSafeInteger(output) && output > 0 ? output : null;
}
function date(input: unknown): string {
  const raw = value(input);
  return raw ? new Date(raw).toLocaleString() : "";
}
function capitalize(input: string): string {
  return input ? input[0]!.toUpperCase() + input.slice(1) : "";
}
function weekday(day: number): string {
  return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][day] ?? "?";
}
